import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, debounce, SvgIcon } from '@mui/material';
import TrainingManagementStatus from './components/TrainingManagementStatus';
import TrainingProgramsList from './components/TrainingPrograms';
import { TrainingPorgramsService } from 'services/TrainingProgramService';
import { Utils } from 'shared/helpers';
import PageHeader from 'shared/components/PageHeader';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

const TrainingPrograms = (): ReactElement => {
  const dispatch = useDispatch();
  const programsPagination = useSelector(
    (state: IAppState) => state.paginationState.programs
  );
  const [trainingData, setTrainingData] = useState([]);
  const [boardData, setBoardData] = useState<any>(null);
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const navigate = useNavigate();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const handleGetTrainingProgramList = (): void => {
    const queryString = new URLSearchParams({
      offset: String(programsPagination.offset),
      limit: String(programsPagination.pageLimit),
      title: programsPagination.search,
      status: programsPagination.status,
      domain: programsPagination.domain,
    }).toString();

    TrainingPorgramsService.getTrainingPrograms(queryString)
      .then((response) => {
        const { data } = response.data;
        setTrainingData(data?.programs);
        const count = data?.count;
        const totalAssignments = data?.totalAssignments;
        const totalCompleted = data?.totalCompleted;
        const totalOverdue = data?.totalOverdue;
        const totalPrograms = data?.totalPrograms;
        const completionRatio = data?.completionRatio;

        const activeCount = data?.programs.filter(
          (item: { isActive: any }) => item.isActive
        ).length;

        setBoardData({
          count,
          activeCount,
          totalAssignments,
          totalCompleted,
          totalOverdue,
          totalPrograms,
          completionRatio,
        });

        setLoadingData(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const debouncSearch = useCallback(
    debounce((searchValue: string) => {
      dispatch(
        PaginationActions.setPagination({
          programs: {
            ...programsPagination,
            offset: 0,
            search: searchValue,
          },
        })
      );
    }, 500),
    [programsPagination]
  );

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        programs: {
          ...programsPagination,
          offset: 0,
          status: statusValue === 'All Status' ? '' : statusValue,
        },
      })
    );
  };

  const handleDomainChange = (domainValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        programs: {
          ...programsPagination,
          offset: 0,
          domain: domainValue === 'All Domains' ? '' : domainValue,
        },
      })
    );
  };

  const handleClearFilter = (): void => {
    dispatch(
      PaginationActions.setPagination({
        programs: {
          ...programsPagination,
          offset: 0,
          search: '',
          status: '',
          domain: '',
        },
      })
    );
  };

  useEffect(() => {
    handleGetTrainingProgramList();
  }, [
    programsPagination.search,
    programsPagination.status,
    programsPagination.domain,
  ]);

  return (
    <Box>
      <PageHeader
        title="Training Management"
        description="Manage AI training programs and track compliance across your organisation"
        actions={
          (orgACLConfig.training?.includes(
            'training.trainingPrograms.addNewTrainingProgram'
          ) && (
            <AppPrimaryButton
              className="btn-small"
              onClick={() => navigate('/training-programs/training')}
              startIcon={
                <SvgIcon
                  component={Plus}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                />
              }
              title="New Training Program"
            />
          )) || <></>
        }
      />
      <Box className="training training-program">
        <HeadTag title="Training Programs" />

        <Box className="training-wrapper">
          <TrainingManagementStatus
            totalPrograms={boardData?.totalPrograms ?? 0}
            assignmentStatus={boardData?.totalAssignments}
            totalCompleted={boardData?.totalCompleted}
            completionRate={boardData?.completionRatio}
            overdue={boardData?.totalOverdue}
            activeCount={boardData?.activeCount ?? 0}
          />
          <TrainingProgramsList
            programs={undefined}
            trainingData={trainingData}
            loadingData={loadingData}
            handleUpdateList={handleGetTrainingProgramList}
            debouncSearch={debouncSearch}
            handleStatusChange={handleStatusChange}
            handleDomainChange={handleDomainChange}
            handleClearFilter={handleClearFilter}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TrainingPrograms;
